import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateSCGRule } from '../services/api';
import { 
  XMarkIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const EditSCGRuleModal = ({ isOpen, onClose, rule }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // Initialize form data when rule changes
  useEffect(() => {
    if (rule) {
      setFormData({
        rule_id: rule.rule_id || '',
        classification_level: rule.classification_level || '',
        item: rule.item || '',
        description: rule.description || '',
        keywords: Array.isArray(rule.keywords) ? rule.keywords.join(', ') : (rule.keywords || ''),
        derivative_citation: rule.derivative_citation || '',
        compartment_caveats: Array.isArray(rule.compartment_caveats) ? rule.compartment_caveats.join(', ') : (rule.compartment_caveats || ''),
        dissemination_control: Array.isArray(rule.dissemination_control) ? rule.dissemination_control.join(', ') : (rule.dissemination_control || ''),
        reason: rule.reason || '',
        declassify_on: rule.declassify_on || '',
        remarks: rule.remarks || '',
        source: rule.source || '',
        full_classification: rule.full_classification || '',
        caveats: Array.isArray(rule.caveats) ? rule.caveats.join(', ') : (rule.caveats || ''),
        releasability: Array.isArray(rule.releasability) ? rule.releasability.join(', ') : (rule.releasability || '')
      });
      setErrors({});
    }
  }, [rule]);

  const updateMutation = useMutation({
    mutationFn: ({ ruleId, updateData }) => updateSCGRule(ruleId, updateData),
    onSuccess: () => {
      // Invalidate and refetch SCG rules data
      queryClient.invalidateQueries(['scgRules']);
      queryClient.invalidateQueries(['scgSources']);
      onClose();
    },
    onError: (error) => {
      console.error('Update failed:', error);
      setErrors({ general: error.response?.data?.detail || 'Failed to update rule' });
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.rule_id?.trim()) {
      newErrors.rule_id = 'Rule ID is required';
    }
    if (!formData.classification_level?.trim()) {
      newErrors.classification_level = 'Classification level is required';
    }
    if (!formData.item?.trim()) {
      newErrors.item = 'Item is required';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prepare update data, converting comma-separated strings back to arrays where needed
    const updateData = { ...formData };
    
    // Remove rule_id from update data (it's in the URL)
    delete updateData.rule_id;
    
    // Convert comma-separated strings to arrays for array fields
    const arrayFields = ['keywords', 'compartment_caveats', 'dissemination_control', 'caveats', 'releasability'];
    arrayFields.forEach(field => {
      if (updateData[field] && typeof updateData[field] === 'string') {
        updateData[field] = updateData[field].split(',').map(item => item.trim()).filter(item => item);
      }
    });

    // Convert empty strings to null for optional fields
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === '') {
        updateData[key] = null;
      }
    });


    updateMutation.mutate({
      ruleId: rule.rule_id,
      updateData
    });
  };

  if (!isOpen || !rule) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <PencilIcon className="w-6 h-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Edit SCG Rule: {rule.rule_id}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {errors.general && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <ExclamationCircleIcon className="w-5 h-5 text-red-400 mr-2" />
              <p className="text-sm text-red-700">{errors.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Rule ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule ID *
              </label>
              <input
                type="text"
                value={formData.rule_id || ''}
                onChange={(e) => handleInputChange('rule_id', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.rule_id ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter rule ID"
              />
              {errors.rule_id && (
                <p className="mt-1 text-sm text-red-600">{errors.rule_id}</p>
              )}
            </div>

            {/* Classification Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classification Level *
              </label>
              <select
                value={formData.classification_level || ''}
                onChange={(e) => handleInputChange('classification_level', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.classification_level ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select classification level</option>
                <option value="UNCLASSIFIED">UNCLASSIFIED</option>
                <option value="CONFIDENTIAL">CONFIDENTIAL</option>
                <option value="SECRET">SECRET</option>
                <option value="TOP SECRET">TOP SECRET</option>
              </select>
              {errors.classification_level && (
                <p className="mt-1 text-sm text-red-600">{errors.classification_level}</p>
              )}
            </div>
          </div>

          {/* Item */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Item *
            </label>
            <input
              type="text"
              value={formData.item || ''}
              onChange={(e) => handleInputChange('item', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.item ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter item"
            />
            {errors.item && (
              <p className="mt-1 text-sm text-red-600">{errors.item}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter description"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Keywords <span className="text-gray-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.keywords || ''}
                onChange={(e) => handleInputChange('keywords', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>

            {/* Source */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <input
                type="text"
                value={formData.source || ''}
                onChange={(e) => handleInputChange('source', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Source document"
              />
            </div>
          </div>

          {/* Derivative Citation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Derivative Citation
            </label>
            <input
              type="text"
              value={formData.derivative_citation || ''}
              onChange={(e) => handleInputChange('derivative_citation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter derivative citation"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Compartment Caveats */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compartment Caveats <span className="text-gray-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.compartment_caveats || ''}
                onChange={(e) => handleInputChange('compartment_caveats', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="SI, HCS, TK"
              />
            </div>

            {/* Dissemination Control */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dissemination Control <span className="text-gray-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.dissemination_control || ''}
                onChange={(e) => handleInputChange('dissemination_control', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="NOFORN, ORCON, FOUO"
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason
            </label>
            <textarea
              value={formData.reason || ''}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Classification reason"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Declassify On */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Declassify On
              </label>
              <input
                type="text"
                value={formData.declassify_on || ''}
                onChange={(e) => handleInputChange('declassify_on', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="25X1, OADR, date"
              />
            </div>

            {/* Full Classification */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Classification
              </label>
              <input
                type="text"
                value={formData.full_classification || ''}
                onChange={(e) => handleInputChange('full_classification', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Full classification marking"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Caveats */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Caveats <span className="text-gray-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.caveats || ''}
                onChange={(e) => handleInputChange('caveats', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional caveats"
              />
            </div>

            {/* Releasability */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Releasability <span className="text-gray-500">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={formData.releasability || ''}
                onChange={(e) => handleInputChange('releasability', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="USA, CAN, GBR, AUS, NZL"
              />
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              value={formData.remarks || ''}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Additional remarks"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {updateMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  Update Rule
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSCGRuleModal;